import { useQuery } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';
import { qk } from '../queries/keys';
import { fetchTags, type TagChoice, type TagWithCount } from '../queries/tags';
import { PlusIcon } from './icons';
import { cx } from './ui';

/**
 * The tag combobox: type, see what matches, pick one, or make the word you
 * typed into a new tag. The list is this person's own tags and their
 * team's; a team tag is offered for making only where the recording is the
 * team's, since the API would refuse it anywhere else.
 *
 * Keyboard: arrows move, Enter picks, Escape closes. What Enter picks with
 * nothing highlighted is the exact match if there is one, otherwise the
 * first row, which is the closest match or the offer to make the word.
 */

type Row =
  | { kind: 'tag'; tag: TagWithCount }
  | { kind: 'create'; name: string; scope: 'personal' | 'team'; teamName: string | null };

export function TagPicker({
  exclude,
  canCreateTeam,
  onPick,
  placeholder = 'Add a tag',
  autoFocus = false,
  disabled = false,
  create = true,
}: {
  /** Tags already applied, left out of the list. */
  exclude: string[];
  /** The recording is the team's, so a team tag may be made for it. */
  canCreateTeam: boolean;
  onPick: (choice: TagChoice, row: { name: string; scope: 'personal' | 'team'; created: boolean }) => void;
  placeholder?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  /** Offer to make a new tag from what was typed. Off for a filter. */
  create?: boolean;
}) {
  const [text, setText] = useState('');
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const root = useRef<HTMLDivElement>(null);
  const listId = useId();

  useEffect(() => {
    const handle = window.setTimeout(() => setTerm(text.trim()), 150);
    return () => window.clearTimeout(handle);
  }, [text]);

  const query = useQuery({ queryKey: qk.tags(term), queryFn: () => fetchTags(term), staleTime: 15_000, enabled: open });
  const team = query.data?.team ?? null;
  const matches = (query.data?.tags ?? []).filter((tag) => !exclude.includes(tag.id));
  const typed = text.trim();
  const exact = matches.find((tag) => tag.name.toLowerCase() === typed.toLowerCase());

  const rows: Row[] = matches.map((tag) => ({ kind: 'tag', tag }));
  if (create && typed && !exact && typed.length <= 40) {
    rows.push({ kind: 'create', name: typed, scope: 'personal', teamName: null });
    if (team && canCreateTeam) rows.push({ kind: 'create', name: typed, scope: 'team', teamName: team.name });
  }

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const pick = (row: Row) => {
    if (row.kind === 'tag') onPick({ tagId: row.tag.id }, { name: row.tag.name, scope: row.tag.scope, created: false });
    else onPick({ name: row.name, scope: row.scope }, { name: row.name, scope: row.scope, created: true });
    setText('');
    setTerm('');
    setActive(-1);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActive((current) => Math.min(current + 1, rows.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((current) => Math.max(current - 1, -1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const row = active >= 0 ? rows[active] : exact ? rows.find((one) => one.kind === 'tag' && one.tag.id === exact.id) : rows[0];
      if (row) pick(row);
    } else if (event.key === 'Escape') {
      setOpen(false);
      setActive(-1);
    }
  };

  return (
    <div ref={root} className="relative">
      <input
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
        aria-label={placeholder}
        value={text}
        disabled={disabled}
        placeholder={placeholder}
        maxLength={40}
        autoFocus={autoFocus}
        autoComplete="off"
        onChange={(event) => {
          setText(event.target.value);
          setOpen(true);
          setActive(-1);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        className={cx(
          'h-8 w-full min-w-0 rounded-xs border border-chalk/20 bg-slate-950/70 px-2 text-caption text-chalk',
          'placeholder:text-chalk-faint focus:border-signal focus:outline-none disabled:opacity-40',
        )}
      />
      {open && (rows.length > 0 || query.isPending) ? (
        <ul
          id={listId}
          role="listbox"
          aria-label="Tags"
          className="absolute left-0 top-full z-30 mt-1 max-h-64 w-full min-w-[14rem] overflow-auto rounded-md border border-hairline bg-slate-850 p-1 shadow-raised"
        >
          {rows.map((row, index) => (
            <li
              key={row.kind === 'tag' ? row.tag.id : `${row.scope}:${row.name}`}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === active}
              onMouseDown={(event) => {
                // Before the input blurs, so the outside-click handler does not close us first.
                event.preventDefault();
                pick(row);
              }}
              onMouseEnter={() => setActive(index)}
              className={cx(
                'flex cursor-pointer items-center gap-2 rounded px-2.5 py-1.5 text-caption',
                index === active ? 'bg-slate-800 text-chalk' : 'text-chalk-dim',
              )}
            >
              {row.kind === 'tag' ? (
                <>
                  <span className="truncate text-chalk">{row.tag.name}</span>
                  <span className="ml-auto shrink-0 text-micro text-chalk-faint">
                    {row.tag.scope === 'team' ? 'team' : 'only you'}
                    {row.tag.videoCount > 0 ? ` · ${row.tag.videoCount}` : ''}
                  </span>
                </>
              ) : (
                <>
                  <PlusIcon size={12} className="shrink-0" />
                  <span className="truncate">
                    Create <span className="text-chalk">{row.name}</span>
                    {row.scope === 'team' ? ` for ${row.teamName}` : ', just for you'}
                  </span>
                </>
              )}
            </li>
          ))}
          {rows.length === 0 && query.isPending ? <li className="px-2.5 py-1.5 text-caption text-chalk-faint">Looking</li> : null}
        </ul>
      ) : null}
    </div>
  );
}
